/*
 * "Where people go." (sections/dte-follow-pin.liquid): follow the pin. Plain script, no library.
 * Justified in docs/decisions-log.md (2026-09-28): scroll-scrubbed motion needs a scroll handler.
 *
 * Builds a tall scroll track with a sticky stage. The stops' screens wipe up one after another; a
 * sea-blue dot drives a wattle route between their pins on our night map; the pin it reaches lands
 * with a ripple and gets a numbered label. The map is big on the intro and moves to its place for the
 * stops (a corner card over photos, a band over place stops on phones), and its camera can follow
 * the dot. Everything is placed from the stops' own data attributes: no town or maker is named here.
 *
 * Reduced motion: nothing is scrubbed; a stop swaps in at the halfway point and the dot jumps.
 * Without this file the section stays a plain list with the whole map.
 */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var NS = 'http://www.w3.org/2000/svg';

  function clamp(v, a, b) {
    return Math.max(a === undefined ? 0 : a, Math.min(b === undefined ? 1 : b, v));
  }
  function ease(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }
  function lerp(a, b, t) {
    return a + (b - a) * t;
  }
  function lerpRect(a, b, t) {
    return { x: lerp(a.x, b.x, t), y: lerp(a.y, b.y, t), w: lerp(a.w, b.w, t), h: lerp(a.h, b.h, t) };
  }

  function init(sec) {
    if (sec.dataset.fpReady) return;
    var track = sec.querySelector('.dte-fp__track');
    var stage = sec.querySelector('.dte-fp__stage');
    var intro = sec.querySelector('[data-fp-intro]');
    var map = sec.querySelector('[data-fp-map]');
    var svg = sec.querySelector('[data-fp-svg]');
    var routesG = sec.querySelector('[data-fp-routes]');
    var car = sec.querySelector('[data-fp-car]');
    var ghost = sec.querySelector('[data-fp-ghost]');
    var pill = sec.querySelector('[data-fp-pill]');
    var pillN = sec.querySelector('[data-fp-pill-n]');
    var pillName = sec.querySelector('[data-fp-pill-name]');
    var scrs = Array.prototype.slice.call(sec.querySelectorAll('[data-fp-scr]'));
    if (!track || !stage || !map || !svg || scrs.length < 2) return;
    sec.dataset.fpReady = '1';

    // Horizon scrolls .page-wrapper, not the window, on some screens: follow whichever scrolls
    var sc = null;
    for (var el = sec.parentElement; el && el !== document.body; el = el.parentElement) {
      var o = getComputedStyle(el).overflowY;
      if (o === 'auto' || o === 'scroll') { sc = el; break; }
    }
    function viewTop() { return sc ? sc.getBoundingClientRect().top : 0; }

    var coastEls = Array.prototype.slice.call(svg.querySelectorAll('.dte-fp__coast'));
    var contEls = Array.prototype.slice.call(svg.querySelectorAll('.dte-fp__cont'));
    var townEls = Array.prototype.slice.call(svg.querySelectorAll('[data-fp-towns] circle'));
    var towns = townEls.map(function (c) { return { x: +c.getAttribute('cx'), y: +c.getAttribute('cy') }; });

    // Stops, in order; the last screen is the free one
    var stops = [];
    var last = null;
    scrs.forEach(function (s, k) {
      var free = s.hasAttribute('data-free');
      var x = parseFloat(s.getAttribute('data-x'));
      var y = parseFloat(s.getAttribute('data-y'));
      var has = !isNaN(x) && !isNaN(y);
      if (!has && last) { x = last.x; y = last.y; }
      var st = {
        el: s,
        free: free,
        x: has ? x : (last ? last.x : 400),
        y: has ? y : (last ? last.y : 480),
        name: s.getAttribute('data-name') || '',
        photo: s.classList.contains('dte-fp__scr--maker') || s.classList.contains('dte-fp__scr--cat'),
        pin: free ? ghost : sec.querySelector('[data-fp-pin="' + (k + 1) + '"]'),
        txt: s.querySelector('.dte-fp__txt'),
        imgs: Array.prototype.slice.call(s.querySelectorAll('.dte-fp__ph img, .dte-fp__art'))
      };
      if (has && !free) last = st;
      stops.push(st);
    });
    var N = stops.length;
    var real = stops.filter(function (s) { return !s.free; });

    // The free stop: an empty pin at the nearest town that has no stop, a short hop from the last one
    var freeStop = stops[N - 1].free ? stops[N - 1] : null;
    if (freeStop && last) {
      var best = null;
      var bd = 1e9;
      towns.forEach(function (t) {
        var clear = real.every(function (s) { return Math.hypot(s.x - t.x, s.y - t.y) > 36; });
        var d = Math.hypot(t.x - last.x, t.y - last.y);
        if (clear && d < bd) { bd = d; best = t; }
      });
      freeStop.x = best ? best.x : last.x + 60;
      freeStop.y = best ? best.y : last.y;
    }

    // Route: a gentle curve per leg, bowed towards the land (east and south of this coast)
    function ctrl(a, b) {
      var mx = (a.x + b.x) / 2;
      var my = (a.y + b.y) / 2;
      var nx = -(b.y - a.y);
      var ny = b.x - a.x;
      if (nx + ny < 0) { nx = -nx; ny = -ny; }
      return { x: mx + nx * 0.22, y: my + ny * 0.22 };
    }
    var legs = [];
    for (var i = 0; i < N - 1; i++) {
      var a = stops[i];
      var b = stops[i + 1];
      var c = ctrl(a, b);
      var d = 'M' + a.x + ' ' + a.y + 'Q' + c.x + ' ' + c.y + ' ' + b.x + ' ' + b.y;
      var bg = document.createElementNS(NS, 'path');
      bg.setAttribute('class', 'dte-fp__route-bg');
      bg.setAttribute('d', d);
      var fg = document.createElementNS(NS, 'path');
      fg.setAttribute('class', 'dte-fp__route');
      fg.setAttribute('d', d);
      fg.setAttribute('pathLength', '1');
      fg.setAttribute('stroke-dasharray', '1 1');
      fg.setAttribute('stroke-dashoffset', '1');
      routesG.appendChild(bg);
      legs.push({ a: a, b: b, c: c, bg: bg, fg: fg, drawn: -1 });
    }
    legs.forEach(function (l) { routesG.appendChild(l.fg); });
    function onLeg(l, u) {
      var v = 1 - u;
      return { x: v * v * l.a.x + 2 * v * u * l.c.x + u * u * l.b.x, y: v * v * l.a.y + 2 * v * u * l.c.y + u * u * l.b.y };
    }

    // Camera: the box holding every stop, padded, shaped to the map's frame
    var ext = stops.reduce(function (m, s) {
      return { x0: Math.min(m.x0, s.x), x1: Math.max(m.x1, s.x), y0: Math.min(m.y0, s.y), y1: Math.max(m.y1, s.y) };
    }, { x0: 1e9, x1: -1e9, y0: 1e9, y1: -1e9 });
    function fit(rect) {
      var pad = 40 + 0.1 * Math.max(ext.x1 - ext.x0, ext.y1 - ext.y0);
      var x0 = ext.x0 - pad;
      var x1 = ext.x1 + pad;
      var y0 = ext.y0 - pad * 1.5;
      var y1 = ext.y1 + pad * 0.6;
      var w = x1 - x0;
      var h = y1 - y0;
      var ar = rect.w / rect.h;
      if (w / h < ar) { var nw = h * ar; x0 -= (nw - w) / 2; w = nw; } else { var nh = w / ar; y0 -= (nh - h) / 2; h = nh; }
      return { x: x0, y: y0, w: w, h: h };
    }
    function follow(rect, at, whole) {
      var w = clamp(whole.w * 0.5, 200, 900);
      var h = w * rect.h / rect.w;
      return { x: at.x - w / 2, y: at.y - h * 0.55, w: w, h: h };
    }

    // Frames for the map, measured from the stage
    var W = 0;
    var H = 0;
    var desk = false;
    var R = {};
    var step = 0;
    var dist = 0;
    // The site header stays on screen (sticky): keep the map and the words clear of it
    function headerH() {
      var hmax = 0;
      Array.prototype.forEach.call(document.querySelectorAll('.shopify-section-group-header-group, header'), function (e) {
        var ps = getComputedStyle(e).position;
        if (ps === 'sticky' || ps === 'fixed') hmax = Math.max(hmax, e.getBoundingClientRect().height);
      });
      return Math.min(hmax, 140);
    }
    function measure() {
      W = stage.clientWidth;
      H = stage.clientHeight;
      desk = W >= 900;
      var top = headerH();
      sec.style.setProperty('--fp-top', Math.round(top) + 'px');
      var g = parseFloat(getComputedStyle(sec).getPropertyValue('--gutter')) || (desk ? 48 : 16);
      if (!desk) {
        intro.style.setProperty('--ity', '0px');
        var ib = intro.getBoundingClientRect();
        var sb = stage.getBoundingClientRect();
        var iy = ib.bottom - sb.top + 14;
        var ih = Math.max(120, H - iy - 18);
        R.intro = { x: g, y: Math.min(iy, H - 120 - 18), w: W - 2 * g, h: ih };
        var cw = Math.min(W * (H - top < 560 ? 0.36 : 0.42), 200);
        R.card = { x: W - g - cw, y: top + 12, w: cw, h: cw * 1.12 };
        var txtMax = 0;
        stops.forEach(function (s) { if (!s.photo && !s.free && s.txt) txtMax = Math.max(txtMax, s.txt.offsetHeight); });
        var bh = clamp(H - top - txtMax - 12 - 24 - 20, 130, H * 0.5);
        R.band = { x: g, y: top + 12, w: W - 2 * g, h: bh };
      } else {
        var mh = Math.min(H - top - 72, 760);
        var mw = Math.min(W * 0.4, 620, mh * 0.86);
        mh = Math.min(mh, mw * 1.25);
        R.intro = { x: W - g - mw, y: top + (H - top - mh) / 2, w: mw, h: mh };
        R.band = R.intro;
        R.card = { x: W - g - 300, y: top + 24, w: 300, h: 320 };
      }
      stops.forEach(function (s) {
        s.rect = s.photo || s.free ? R.card : R.band;
        s.followAmt = s.free ? 0 : (s.rect === R.card ? 0.55 : (desk ? 0 : 1));
      });
      step = H * (desk ? 0.95 : 0.85);
      dist = (N + 0.4) * step;
      sec.style.setProperty('--track-h', Math.round(H + dist) + 'px');
    }

    var pinPx = 34;
    var cur = -2;
    var pinged = {};
    var placed = '';
    function drawMap(rect, cam, carAt, showCar, curIdx, hitUpTo) {
      var key = [rect.x, rect.y, rect.w, rect.h].map(Math.round).join(',');
      if (key !== placed) {
        placed = key;
        map.style.transform = 'translate(' + rect.x + 'px,' + rect.y + 'px)';
        map.style.width = rect.w + 'px';
        map.style.height = rect.h + 'px';
      }
      svg.setAttribute('viewBox', cam.x.toFixed(1) + ' ' + cam.y.toFixed(1) + ' ' + cam.w.toFixed(1) + ' ' + cam.h.toFixed(1));
      var ppu = rect.w / cam.w;
      var u = 1 / ppu;
      pinPx = clamp(rect.w * 0.1, 26, 54);
      var s = pinPx / 84 * u;
      stops.forEach(function (st, k) {
        if (!st.pin) return;
        st.pin.setAttribute('transform', 'translate(' + st.x + ' ' + st.y + ') scale(' + s.toFixed(4) + ')');
        var on = k === curIdx;
        st.pin.classList.toggle('is-on', on);
        st.pin.classList.toggle('is-hit', k <= hitUpTo);
      });
      coastEls.forEach(function (e) { e.setAttribute('stroke-width', (2.2 * u).toFixed(2)); });
      contEls.forEach(function (e) {
        e.setAttribute('stroke-width', (1.4 * u).toFixed(2));
        e.setAttribute('stroke-dasharray', (2 * u).toFixed(2) + ' ' + (10 * u).toFixed(2));
      });
      townEls.forEach(function (e) { e.setAttribute('r', (3.2 * u).toFixed(2)); });
      legs.forEach(function (l) {
        l.bg.setAttribute('stroke-width', (2.4 * u).toFixed(2));
        l.bg.setAttribute('stroke-dasharray', (1 * u).toFixed(2) + ' ' + (8 * u).toFixed(2));
        l.fg.setAttribute('stroke-width', (4.5 * u).toFixed(2));
      });
      car.setAttribute('transform', 'translate(' + carAt.x.toFixed(1) + ' ' + carAt.y.toFixed(1) + ') scale(' + u.toFixed(4) + ')');
      car.style.opacity = showCar ? 1 : 0;

      // The label beside the current pin
      var st = stops[curIdx];
      if (st && rect.w >= 180) {
        if (pill.__k !== curIdx || pill.hidden) {
          pillN.textContent = String(curIdx + 1);
          pillName.textContent = st.name;
          pill.hidden = false;
          pill.__k = curIdx;
          pill.__w = pill.offsetWidth;
          pill.__h = pill.offsetHeight;
        }
        var sx = (st.x - cam.x) * ppu;
        var sy = (st.y - cam.y) * ppu;
        var pw = pill.__w;
        var ph = pill.__h;
        // Beside the pin head, right then left; if neither fits, under the pin's tip. Never over the pin.
        var head = sy - pinPx * 0.66;
        var lx = sx + pinPx * 0.42;
        var ly = head - ph / 2;
        if (lx + pw > rect.w - 6) {
          lx = sx - pinPx * 0.42 - pw;
          if (lx < 6) { lx = sx - pw / 2; ly = sy + 8; }
        }
        pill.style.transform = 'translate(' + Math.round(clamp(lx, 4, Math.max(4, rect.w - pw - 4))) + 'px,' + Math.round(clamp(ly, 4, rect.h - ph - 4)) + 'px)';
      } else {
        pill.hidden = true;
      }
    }

    function scrollPos() {
      var r = track.getBoundingClientRect();
      return viewTop() - r.top;
    }

    function frame() {
      if (!step) return;
      var p = clamp(scrollPos() / dist) * (N + 0.4) - 1;
      var f = Math.min(p, N - 1);
      var ti = ease(clamp(f + 1));
      var idx = Math.max(0, Math.floor(f));
      if (idx > N - 1) idx = N - 1;
      var t = f < 0 ? 0 : f - idx;
      var w = reduce ? (t > 0.5 ? 1 : 0) : ease(clamp((t - 0.45) / 0.55));
      if (idx >= N - 1) w = 0;

      // Intro words leave as the first stop arrives
      intro.style.opacity = String(1 - clamp((f + 1) * 1.6));
      intro.style.setProperty('--ity', (-40 * ti) + 'px');
      intro.style.pointerEvents = f > -0.35 ? 'none' : '';

      // Screens: the next wipes up over the last
      scrs.forEach(function (el, k) {
        var top = 100;
        if (f < 0) {
          if (k === 0) top = reduce ? (f > -0.5 ? 0 : 100) : 100 - ease(clamp((f + 1 - 0.35) / 0.65)) * 100;
        } else if (k <= idx) {
          top = 0;
        } else if (k === idx + 1) {
          top = (1 - w) * 100;
        }
        var cp = 'inset(' + top.toFixed(2) + '% 0 0 0)';
        if (el.__cp !== cp) { el.__cp = cp; el.style.clipPath = cp; el.style.webkitClipPath = cp; }
        var st = stops[k];
        if (st.txt) {
          var vis = k === idx + 1 ? w : (k === idx ? 1 - clamp((w - 0.2) * 1.5) : 0);
          if (f < 0 && k === 0) vis = clamp((f + 0.4) / 0.4);
          st.txt.style.setProperty('--op', vis.toFixed(3));
          st.txt.style.setProperty('--ty', ((1 - (k === idx + 1 ? w : 1)) * 36).toFixed(1) + 'px');
        }
        if (!reduce && st.imgs.length) {
          var local = k === idx ? t : (k === idx + 1 ? w - 1 : 0);
          var z = (1.07 - 0.06 * clamp(local + 0.5)).toFixed(4);
          st.imgs.forEach(function (im) { im.style.setProperty('--z', z); });
        }
      });

      // Where the map sits: intro frame, then each stop's own frame
      var from = stops[idx];
      var to = stops[Math.min(idx + 1, N - 1)];
      var rect = f < 0 ? lerpRect(R.intro, stops[0].rect, ti) : lerpRect(from.rect, to.rect, w);
      var whole = fit(rect);

      // The dot
      var at = { x: stops[0].x, y: stops[0].y };
      if (f >= 0 && idx < N - 1) at = onLeg(legs[idx], w);
      else if (f >= 0) at = { x: stops[N - 1].x, y: stops[N - 1].y };
      var fa = f < 0 ? stops[0].followAmt * ti : lerp(from.followAmt, to.followAmt, w);
      var cam = whole;
      if (fa > 0) {
        var fc = follow(rect, at, whole);
        cam = { x: lerp(whole.x, fc.x, fa), y: lerp(whole.y, fc.y, fa), w: lerp(whole.w, fc.w, fa), h: lerp(whole.h, fc.h, fa) };
      }

      // Route drawn up to the dot
      legs.forEach(function (l, k) {
        var dv = k < idx ? 1 : (k === idx && f >= 0 ? w : 0);
        if (l.drawn !== dv) { l.drawn = dv; l.fg.setAttribute('stroke-dashoffset', (1 - dv).toFixed(4)); }
      });

      var current = f < -0.3 ? -1 : (w > 0.5 ? Math.min(idx + 1, N - 1) : idx);
      var hitUpTo = f < -0.3 ? N : current;
      drawMap(rect, cam, at, f > -0.3, current, hitUpTo);
      if (ghost) ghost.classList.toggle('is-on', current === N - 1);

      // The pin the dot reaches lands with a ripple, once per arrival
      if (current !== cur) {
        if (current > cur && current >= 0 && stops[current].pin && !stops[current].free) {
          var pin = stops[current].pin;
          pin.classList.remove('is-ping');
          void pin.getBoundingClientRect();
          pin.classList.add('is-ping');
          clearTimeout(pinged[current]);
          pinged[current] = setTimeout(function () { pin.classList.remove('is-ping'); }, 1500);
        }
        cur = current;
      }
    }

    var raf = 0;
    function onScroll() {
      if (raf) return;
      raf = requestAnimationFrame(function () { raf = 0; frame(); });
    }

    sec.classList.add('is-driving');
    measure();
    frame();
    (sc || window).addEventListener('scroll', onScroll, { passive: true });
    var lastW = W;
    var lastH = H;
    window.addEventListener('resize', function () {
      var w2 = stage.clientWidth;
      var h2 = stage.clientHeight;
      if (Math.abs(w2 - lastW) < 2 && Math.abs(h2 - lastH) < 40) return;
      lastW = w2;
      lastH = h2;
      measure();
      placed = '';
      frame();
    });

    // Pins drop in once, in a stagger, the first time the map is in view
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (es) {
        if (!es.some(function (e) { return e.isIntersecting; })) return;
        io.disconnect();
        stops.forEach(function (s, k) {
          if (!s.pin || s.free) return;
          s.pin.style.setProperty('--d', (k * 140 + 150) + 'ms');
          s.pin.classList.add('is-drop');
        });
      }, { threshold: 0.3 });
      io.observe(map);
    }

    // Keyboard: focusing a link in a stop brings that stop into view
    sec.addEventListener('focusin', function (e) {
      var s = e.target.closest('[data-fp-scr]');
      var k = scrs.indexOf(s);
      if (k < 0 && !intro.contains(e.target)) return;
      var want = k < 0 ? 0 : ((k + 1.2) / (N + 0.4)) * dist;
      var now = scrollPos();
      if (Math.abs(now - want) < step * 0.3) return;
      var dy = want - now;
      if (sc) sc.scrollTop += dy;
      else window.scrollTo(0, window.pageYOffset + dy);
      frame();
    });

    // Photo thumbnails swap the photo behind a featured stop
    sec.addEventListener('click', function (e) {
      var b = e.target.closest('[data-fp-thumb]');
      if (!b) return;
      var bs = Array.prototype.slice.call(b.parentNode.querySelectorAll('[data-fp-thumb]'));
      var j = bs.indexOf(b);
      bs.forEach(function (x, i) { x.setAttribute('aria-pressed', i === j ? 'true' : 'false'); });
      var imgs = b.closest('[data-fp-scr]').querySelectorAll('.dte-fp__ph img');
      Array.prototype.forEach.call(imgs, function (im, i) { im.classList.toggle('is-on', i === j); });
    });

    // For QA screenshots: jump to a point in the drive (f from -1 to N - 1)
    sec.__fpAt = function (fv) {
      var want = ((fv + 1) / (N + 0.4)) * dist;
      var dy = want - scrollPos();
      if (sc) sc.scrollTop += dy;
      else window.scrollTo(0, window.pageYOffset + dy);
      frame();
      return N;
    };
  }

  // Set up only when the section comes near (about a screen and a half away), so none of this runs
  // while the page loads. The section is well below the fold, so the track growing then moves
  // nothing on screen.
  function start() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-dte-fp]'), function (sec) {
      if (sec.dataset.fpWatch) return;
      sec.dataset.fpWatch = '1';
      if (!('IntersectionObserver' in window)) { init(sec); return; }
      var io = new IntersectionObserver(function (es) {
        if (!es.some(function (e) { return e.isIntersecting; })) return;
        io.disconnect();
        init(sec);
      }, { rootMargin: '150% 0px 150% 0px' });
      io.observe(sec);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', start);
})();
